import * as React from 'react';
import {makeStyles, darken, CircularProgress} from '@material-ui/core';
import {useQuery} from 'react-query';
import {toast} from 'react-hot-toast';
import {useForm, Controller} from 'react-hook-form';
import {useHistory} from 'react-router-dom';
import * as yup from 'yup';
import {addDays, subDays, differenceInMilliseconds, isValid} from 'date-fns';
import {yupResolver} from '@hookform/resolvers/yup';

import {correctService} from 'services/correctService';
import {CORRECT_ITEM_DELETE_SUCCESS, ERROR_MESSAGE} from 'utils/messages';
import {useAuth} from 'hooks/useAuth';
import {startOfDayTimestamp, getDate} from 'utils/Date';
import {containDigits} from 'utils/String';

import {Typography, Grid, TextField, Select, MenuItem} from '@material-ui/core';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import {Skeleton} from '@material-ui/lab';

import Button from 'components/Button';
import {DATE_FORMAT} from 'utils/regex';
import {useCarrierOptions} from 'hooks/useCarrierOptions';
import CorrectItemCard from 'components/CorrectItemCard';
import {Storage} from 'aws-amplify';
import {getS3ObjectId, parcelStatusOptions, validateSfCode} from 'utils/helper';
import CorrectItemDialog from 'components/CorrectItemDialog';
import Modal from 'components/Modal';
import ScreenSpinner from 'components/ScreenSpinner';
import imagePlaceholder from 'assets/image-placeholder.png';

type FilterInputs = {
  carrier: string;
  createdFrom: Date;
  createdTo: Date;
  uploadedBy: string;
};

const schema = yup.object().shape({
  createdFrom: yup
    .date()
    .typeError('Invalid date')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test(
      'createdToMin',
      'Uploaded From should not be a future date',
      function () {
        // Min date validation
        if (isValid(this.parent.createdFrom)) {
          return (
            differenceInMilliseconds(new Date(), this.parent.createdFrom) >= 0
          );
        }
        return true;
      }
    ),

  createdTo: yup
    .date()
    .typeError('Invalid date')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test(
      'createdToMin',
      'Uploaded To should not be a future date',
      function () {
        // Min date validation
        if (isValid(this.parent.createdTo)) {
          return (
            differenceInMilliseconds(new Date(), this.parent.createdTo) >= 0
          );
        }
        return true;
      }
    )
    .test(
      'createdToMin',
      'Uploaded To cannot be before Uploaded From',
      function () {
        // Min date validation
        if (isValid(this.parent.createdFrom)) {
          return (
            differenceInMilliseconds(
              this.parent.createdTo,
              this.parent.createdFrom
            ) >= 0
          );
        }
        return true;
      }
    ),
});

export type FormattedDataType = {
  id: string;
  image?: string;
  code?: string;
  status: string;
  action: string;
  location?: string;
  updatedAt: string;
  updatedBy: string;
};

const Correct: React.FC = () => {
  const history = useHistory();
  const {authUser} = useAuth();
  const [deleteModal, setDeleteModal] = React.useState<string | null>(null);
  const [editedItems, setEditedItems] = React.useState<FormattedDataType[]>([]);
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [open, setOpen] = React.useState(false);
  const [defaultCarrierId, setDefaultCarrierId] = React.useState('');
  const [errors, setErrors] = React.useState<{[id: string]: {code?: string}}>(
    {}
  );
  const [updating, setUpdating] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(8);
  const [changesPresent, setChangesPresent] = React.useState(false);

  const {carrierOptions, carriersStatus} = useCarrierOptions();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<FilterInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      carrier: '',
      createdFrom: subDays(new Date(startOfDayTimestamp(new Date())), 15),
      createdTo: new Date(startOfDayTimestamp(new Date())),
      uploadedBy: '',
    },
  });

  const classes = useStyles();

  const {data: formattedData = [], refetch, isFetching} = useQuery(
    ['correct', defaultCarrierId],
    async () => {
      const raw = await correctService.list({
        carrier: getValues('carrier'),
        createdFrom: startOfDayTimestamp(getValues('createdFrom')),
        createdTo: startOfDayTimestamp(
          addDays(new Date(getValues('createdTo')), 1)
        ),
        updatedBy: getValues('uploadedBy').trim(),
      });

      return Promise.all(
        raw.map(
          async (item): Promise<FormattedDataType> => {
            const image = item.url
              ? ((await Storage.get(getS3ObjectId(item.url))) as string)
              : undefined;

            const date = new Date(item.updatedAt);
            const formattedDate =
              `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
              `${date.getDate().toString().padStart(2, '0')}/` +
              `${date.getFullYear()}`;

            const updatedBy = `${item.updatedBy.firstName ?? '-'} ${
              item.updatedBy.lastName ?? '-'
            }`;

            return {
              id: item.id,
              image,
              code: item.code,
              status: item.status,
              action: item.action,
              location: item.location,
              updatedAt: formattedDate,
              updatedBy:
                updatedBy.length > 7
                  ? `${updatedBy.slice(0, 7)}...`
                  : updatedBy,
            };
          }
        )
      );
    },
    {
      enabled: !!defaultCarrierId,
      retry: false,
    }
  );

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (authUser?.attributes['custom:user_role'] === 'carrierAdmin') {
      setDefaultCarrierId(authUser?.attributes['custom:carrier_id'] || '');
      setValue('carrier', authUser?.attributes['custom:carrier_id'] || '');
    } else if (carrierOptions?.length > 0) {
      setDefaultCarrierId(carrierOptions[0]?.value);
      setValue('carrier', carrierOptions[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierOptions]);

  const resetFilters = () => {
    reset({
      createdFrom: subDays(new Date(startOfDayTimestamp(new Date())), 15),
      createdTo: new Date(startOfDayTimestamp(new Date())),
      uploadedBy: '',
      carrier: defaultCarrierId,
    });
  };

  const handleClearFilter = async (event: React.MouseEvent) => {
    resetFilters();
    refetch({
      throwOnError: true,
      cancelRefetch: true,
    });
  };

  const onSubmit = async (data: FilterInputs) => {
    refetch({
      throwOnError: true,
      cancelRefetch: true,
    });
  };

  const handleOpen = (index: number) => {
    // Make a fresh editable copy whenever dialog opens
    setEditedItems([...formattedData]);
    setCurrentIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % editedItems.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      prev => (prev - 1 + editedItems.length) % editedItems.length
    );
  };

  const handleChange = (field: keyof FormattedDataType, value: string) => {
    const currentId = editedItems[currentIndex].id;

    setEditedItems(prev => {
      const copy = [...prev];
      copy[currentIndex] = {...copy[currentIndex], [field]: value};
      return copy;
    });

    // Validation rules
    if (field === 'code') {
      let errorMsg: string | undefined;
      if (!value) {
        errorMsg = 'StampFree Code is required';
      } else if (!validateSfCode(value)) {
        errorMsg = 'Invalid StampFree Code';
      }

      setErrors(prev => ({
        ...prev,
        [currentId]: {
          ...prev[currentId],
          code: errorMsg,
        },
      }));
    }

    if (field === 'status') {
      const currentItem = editedItems[currentIndex];
      const codeVal = currentItem.code || '';

      let errorMsg: string | undefined;
      if (!codeVal.trim()) {
        errorMsg = 'StampFree Code is required';
      }

      setErrors(prev => ({
        ...prev,
        [currentId]: {
          ...prev[currentId],
          code: errorMsg,
        },
      }));
    }
  };

  const handleSave = async () => {
    // compare editedItems vs formattedData
    setUpdating(true);
    const changedItems = editedItems.filter(
      (item, idx) => JSON.stringify(item) !== JSON.stringify(formattedData[idx])
    );

    const carrierId = getValues('carrier');

    try {
      await Promise.all(
        changedItems
          .filter(item => !!item.code)
          .map(async item => {
            const {id, code, action} = item;

            await correctService.update({
              carrierId,
              statusUpdateTransactionId: id,
              code: code?.trim()!,
              parcelStatus: action,
            });
          })
      );

      refetch();
      toast.success('All item status updated successfully', {duration: 3000});
    } catch (e) {
      console.log(e);
      toast.error(ERROR_MESSAGE);
    } finally {
      setOpen(false);
      setUpdating(false);
    }
  };

  const hanldeDelete = async () => {
    setDeleteModal(null);
    const carrierId = getValues('carrier');
    await correctService.delete(carrierId, deleteModal!);
    refetch();
    toast.success(CORRECT_ITEM_DELETE_SUCCESS);
  };

  const hasErrors = Object.values(errors).some(
    errObj => Object.values(errObj).some(Boolean) // any non-empty error string
  );

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 8);
  };

  React.useEffect(() => {
    const changedItems = editedItems.filter(
      (item, idx) => JSON.stringify(item) !== JSON.stringify(formattedData[idx])
    );
    setChangesPresent(changedItems.length > 0);
  }, [editedItems]);

  return (
    <div>
      {updating && <ScreenSpinner />}
      <Typography component={'h1'} className={classes.title}>
        Correct
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.filterContainer} spacing={2}>
          <Grid item xs={9}>
            <Typography className={classes.filterText} component={'h2'}>
              Filter
            </Typography>
          </Grid>
          <Grid
            container
            xs={3}
            spacing={2}
            alignItems="center"
            justify="flex-end"
          >
            <Grid item>
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                minWidth="0px"
                onClick={handleClearFilter}
                className={classes.cancelButton}
              >
                Clear
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                minWidth="0px"
                fullWidth
              >
                {'Apply'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.filterOptionContainer} spacing={2}>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Carrier</Typography>
            {carriersStatus === 'loading' ? (
              <Skeleton variant="rect" className={classes.textFieldSkeleton} />
            ) : (
              <Controller
                name="carrier"
                control={control}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    disabled={
                      authUser?.attributes['custom:user_role'] ===
                      'carrierAdmin'
                    }
                    className={classes.select}
                  >
                    {carrierOptions.map(carrier => (
                      <MenuItem value={carrier.value} key={carrier.key}>
                        {carrier.text}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            )}
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Uploaded By</Typography>
            <Controller
              name="uploadedBy"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <TextField
                    variant="outlined"
                    type="text"
                    placeholder="Add Username"
                    fullWidth
                    margin="dense"
                    value={value}
                    onChange={event => {
                      if (containDigits(event.target.value)) return;
                      onChange(event);
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Uploaded From</Typography>
            <Controller
              name="createdFrom"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    value={value}
                    onChange={onChange}
                    maxDate={getDate(watch('createdTo'))}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        fullWidth
                        margin="dense"
                        value={value}
                        onChange={onChange}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        placeholder="Add Uploaded From"
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Uploaded To</Typography>
            <Controller
              name="createdTo"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    minDate={getDate(watch('createdFrom'))}
                    value={value}
                    onChange={onChange}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        fullWidth
                        margin="dense"
                        value={value}
                        onChange={onChange}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        </Grid>
      </form>
      <Typography className={classes.subTitle} component={'h2'}>
        Item Details
      </Typography>
      {isFetching ? (
        <div className={classes.loaderWrapper}>
          <CircularProgress />
        </div>
      ) : formattedData.length ? (
        <>
          <div className={classes.cardsGrid}>
            {formattedData.slice(0, visibleCount).map((data, index) => (
              <div
                style={{cursor: 'pointer'}}
                key={data.id}
                onClick={() => handleOpen(index)}
              >
                <CorrectItemCard
                  image={data.image || imagePlaceholder}
                  id={data.id}
                  code={data.code?.slice(1, data.code.length)}
                  name={data.updatedBy}
                  location={data.location || 'Unknown'}
                  date={data.updatedAt}
                  status={
                    parcelStatusOptions.find(
                      option => option.value === data.action
                    )?.text
                  }
                  onDelete={event => {
                    event.stopPropagation();
                    setDeleteModal(data.id);
                  }}
                />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {visibleCount < formattedData.length && (
            <div className={classes.loadMoreWrapper}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLoadMore}
                className={classes.loadMoreButton}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className={classes.noItems}>No items available</div>
      )}
      <CorrectItemDialog
        open={open}
        editedItems={editedItems}
        currentIndex={currentIndex}
        errors={errors}
        onClose={handleClose}
        onSave={handleSave}
        onPrev={handlePrev}
        onNext={handleNext}
        onChange={handleChange}
        disableSave={hasErrors || !changesPresent}
      />
      <Modal
        open={!!deleteModal}
        handleClose={() => {
          setDeleteModal(null);
        }}
      >
        <>
          <Grid container spacing={3} className={classes.modalHeader}>
            <Grid item xs={7}>
              <Typography component={'h3'} className={classes.modalTitle}>
                Delete Item
              </Typography>
            </Grid>
            <Grid
              container
              xs={5}
              spacing={2}
              alignItems="center"
              justify="flex-end"
            >
              <Grid item>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  minWidth="0px"
                  onClick={() => setDeleteModal(null)}
                  className={classes.cancelButton}
                >
                  {'Close'}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  className={classes.deleteButton}
                  minWidth="0px"
                  fullWidth
                  onClick={hanldeDelete}
                >
                  {'Delete'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item>
              <Typography component={'p'} className={classes.modalText}>
                The image and details will be deleted. Are you sure?
              </Typography>
            </Grid>
          </Grid>
        </>
      </Modal>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 26,
    lineHeight: '40px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      lineHeight: '28px',
    },
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3),
  },
  subTitle: {
    fontSize: 21,
    lineHeight: '32px',
    paddingTop: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      lineHeight: '20px',
    },
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3),
  },
  cancelButton: {
    color: theme.palette.grey[500],
  },
  tabs: {
    marginBottom: '43px',
  },
  tab: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 14,
    lineHeight: '20px',
  },
  filterContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '5px 5px 0 0',
    padding: '12px 16px 15px',
    margin: 0,
    border: '1px solid',
    borderBottom: '0px',
    borderColor: theme.palette.grey[300],
  },
  filterOptionContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '0 0 5px 5px',
    padding: '12px 16px 15px',
    margin: 0,
    marginBottom: theme.spacing(3),
    border: '1px solid',
    borderColor: theme.palette.grey[300],
  },
  filterText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '21px',
    lineHeight: '32px',
  },
  formElement: {},
  formLabel: {
    fontSize: 16,
    lineHeight: '28px',
  },
  hash: {
    backgroundColor: theme.palette.grey[400],
  },
  adornmentTextField: {
    '& span': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  select: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  datePicker: {
    backgroundColor: 'pink',
    '&& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: 'red !important',
      '&:hover': {
        backgroundColor: 'red !important',
      },
      '&:focus': {
        backgroundColor: 'red !important',
      },
    },
  },
  textFieldSkeleton: {
    width: '100%',
    height: 40,
    borderRadius: 4,
    marginTop: 8,
    marginBottom: 4,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  dashboardText: {
    fontSize: 14,
    lineHeight: '20px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      lineHeight: '14px',
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(1),
  },
  subjectText: {
    fontSize: 21,
    lineHeight: '32px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      lineHeight: '24px',
    },
    fontWeight: theme.typography.fontWeightBold,
  },
  subjectInput: {
    margin: 0,
  },
  editButton: {
    minWidth: '0px',
    width: 'auto',
    padding: 9,
    fontSize: 18,
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.light, 0.1),
    },
  },
  updateButton: {
    minWidth: '0px',
    width: 'auto',
  },
  toolbar: {
    borderRadius: '5px',
  },
  editorError: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    margin: '4px 14px 0px',
  },
  content: {
    backgroundColor: '#fff',
    height: 'calc(100vh - 220px)',
    minHeight: '300px',
    padding: 30,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  noItems: {
    textAlign: 'center',
    fontSize: 16,
    color: theme.palette.grey[600],
    marginTop: theme.spacing(2),
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  modalHeader: {
    borderBottom: '1px solid #E0E0E0',
  },
  modalTitle: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 21,
    lineHeight: '32px',
  },
  modalText: {
    fontSize: 18,
    margin: '10px 0px',
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: darken(theme.palette.error.main, 0.1),
      color: theme.palette.common.white,
    },
  },
  loadMoreWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  loadMoreButton: {
    textTransform: 'none',
    borderRadius: 8,
    padding: '6px 24px',
    fontWeight: 500,
  },
}));

export default Correct;
